import React, { useEffect } from "react";
import { QuestionProps } from "../../survey";
import { Answer } from "./generic";
import Checkbox from "../../../components/data_entry/checkbox";
import styled from "styled-components";
import QuestionFieldsetLegend, { StyledFieldset } from "../components/question_fieldset_legend";
import MultiSelectQuestionWrapper, {
  MultiSelectOption,
} from "../../../components/base_ui/higher_order_components/questions/multiselect_question_wrapper";
import { getOptionText, getOptionOid } from "../question_helpers";

const StyledMultiSelect = styled.div`
  margin-top: 2em;
`;

const StyledMultiSelectContainer = styled.div`
  margin-top: ${({ theme }) => theme.sizing.scale600};
`;

export interface MultiSelectCustomization {
  qid: string;
  text: string;
  description?: string;
  optional?: boolean;
  qInfo: {
    type: string;
    options: {
      text: string;
      oid: string;
    }[];
  };
}

export default function MultiSelect({
  customizations,
  currentAnswer,
  updateAnswer,
  setAbleToSubmit,
  showValidation,
  showRefreshedComponents = true,
}: QuestionProps<Answer, MultiSelectCustomization>) {
  const label = customizations.text;
  const secondaryText = customizations.description ?? "";
  const options = customizations.qInfo.options;
  const qid = customizations.qid;
  const optional = typeof customizations.optional === "undefined" ? false : customizations.optional;
  const answerIsValid =
    optional ||
    (currentAnswer && currentAnswer[qid] ? (currentAnswer[qid].length || 0) > 0 : false);
  const selectedOptions = currentAnswer && currentAnswer[qid] ? currentAnswer[qid] : [];

  // Adding setAbleToSubmit as dependency might break survey behavior (e.g. questions that have follow-ups such as the desired_mentoring_frequency question)
  // If you're making edits to this please check that this works with follow-up questions
  useEffect(() => {
    setAbleToSubmit(answerIsValid);
    // eslint-disable-next-line
  }, [answerIsValid, currentAnswer, selectedOptions]);

  const deprecatedMultiSelect = (
    <StyledMultiSelect>
      <StyledFieldset>
        <QuestionFieldsetLegend
          legend={label}
          questionIsOptional={optional}
          description={secondaryText}
        />
        {options.map((option) => (
          <Checkbox
            dataTest="multi-select-checkbox"
            label={option.text}
            key={`${qid}-${option.oid}`}
            checked={selectedOptions.includes(option.oid)}
            onChange={() => {
              const index = selectedOptions.indexOf(option.oid);
              if (index > -1) {
                // The answer is already checked, uncheck it.
                updateAnswer({
                  [qid]: [...selectedOptions.slice(0, index), ...selectedOptions.slice(index + 1)],
                });
              } else {
                // The answer is not checked yet, check it.
                updateAnswer({ [qid]: [...selectedOptions, option.oid] });
              }
            }}
          />
        ))}
      </StyledFieldset>
    </StyledMultiSelect>
  );

  const multiSelect = (
    <StyledMultiSelectContainer>
      <MultiSelectQuestionWrapper
        questionPrompt={label}
        optional={optional}
        secondaryText={secondaryText}
        showErrors={showValidation && !answerIsValid}
        options={options.map((option) => ({
          id: getOptionOid(option) || "",
          label: getOptionText(option) || "",
          value: selectedOptions.includes(option.oid),
        }))}
        onChange={(newAnswer: MultiSelectOption) => {
          const index = selectedOptions.indexOf(newAnswer.id);
          if (index > -1) {
            // The answer is already checked, uncheck it.
            updateAnswer({
              [qid]: [...selectedOptions.slice(0, index), ...selectedOptions.slice(index + 1)],
            });
          } else {
            // The answer is not checked yet, check it.
            updateAnswer({ [qid]: [...selectedOptions, newAnswer.id] });
          }
        }}
        setAbleToSubmit={setAbleToSubmit}
        dataTest="generic-multi-select"
      />
    </StyledMultiSelectContainer>
  );

  return showRefreshedComponents ? multiSelect : deprecatedMultiSelect;
}
